import React, { useState, useRef, useEffect } from 'react';
import { FileUpload, FileUploadFile, FileUploadHeaderTemplateOptions, FileUploadSelectEvent, ItemTemplateOptions } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Document, Page } from 'react-pdf';
import { Image } from 'primereact/image';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';


type Props = {
    label: string;
    icon: React.ReactNode;
    initialFile?: File | FileUploadFile | string | null;
    setInitialFile?: (file: File | FileUploadFile | string | null) => void;
    currentFile?: FileUploadFile | null | undefined;
    onFileSelect: (file: File | null) => void;
    editMode: boolean;
    accept?: string;
}

export default function DocumentFileUpload({ label, icon, initialFile, setInitialFile, currentFile, onFileSelect, editMode, accept }: Props) {
    const fileUploadRef = useRef<FileUpload>(null);
    const [totalSize, setTotalSize] = useState<number>(0);
    const [file, setFile] = useState<any>(null);

    useEffect(() => {
        if (currentFile){
            setFile(currentFile)
            setTotalSize(currentFile.size)
            onFileSelect(currentFile)
            fileUploadRef.current?.setFiles([currentFile])
            return
        }
        if (currentFile === null){
            setFile(null)
            setTotalSize(0)
            onFileSelect(null)
            return
        }
        if (typeof initialFile === 'string') {
            fetch(initialFile)
                .then(res => res.blob())
                .then(blob => {
                    const newFile = new File([blob], initialFile.split('/').pop() || 'file', { type: blob.type });
                    setFile(newFile);
                    setTotalSize(newFile.size);
                    onFileSelect(newFile);
                    const newFileUpload = newFile as FileUploadFile
                    newFileUpload.objectURL = URL.createObjectURL(newFile);
                    fileUploadRef.current?.setFiles([newFileUpload]);
                    setInitialFile&&setInitialFile(newFileUpload)
                });
        } else if (initialFile instanceof File) {
            setFile(initialFile);
            setTotalSize(initialFile.size);
            onFileSelect(initialFile)
            if ('objectURL' in initialFile)
                fileUploadRef.current?.setFiles([initialFile])
        }
    }, [initialFile]);

    const onTemplateSelect = (e: FileUploadSelectEvent) => {
        setTotalSize(e.files[0].size);
        setFile(e.files[0]);
        onFileSelect(e.files[0]);
    };

    const onTemplateRemove = () => {
        setTotalSize(0);
        setFile(null);
        onFileSelect(null);
        fileUploadRef.current?.clear();
    };

    const onTemplateClear = () => {
        setTotalSize(0);
        setFile(null);
        onFileSelect(null);
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const { className, chooseButton, cancelButton } = options;
        const value = totalSize / 30000000 * 100;
        const formatedValue = fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
                {cancelButton}
                <div className="flex align-items-center gap-3 ml-auto">
                    <span>{formatedValue} / 30 MB</span>
                    <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }}></ProgressBar>
                </div>
            </div>
        );
    };

    const itemTemplate = (file: any, props: ItemTemplateOptions) => {
        return (
            <div className="flex align-items-center flex-wrap">
                <div className="flex align-items-center" style={{ width: '40%' }}>
                    {file.type.startsWith('image/') ?
                        <img alt={file.name} role="presentation" src={file.objectURL} width={100} />
                        :
                        <i className="pi pi-file-pdf" style={{ fontSize: '3em' }}></i>
                    }
                    <span className="flex flex-column text-left ml-3">
                        {file.name}
                        <small>{new Date().toLocaleDateString()}</small>
                    </span>
                </div>
                <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={onTemplateRemove} />
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column" style={{ textAlign: 'center' }}>
                <i className="pi pi-file-import mt-3 p-5" style={{
                    fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)',
                }}></i>
                <br></br>
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    Arraste e solte o documento aqui
                </span>
            </div>
        );
    };

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined' };
    const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: true, className: 'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined' };

    return (
        <>
            <Divider align='left'>
                <div className="inline-flex align-items-center" style={{ display: 'flex', alignItems: 'center' }}>{icon}<b> {label}</b></div>
            </Divider>
            <Tooltip target=".custom-choose-btn" content="Selecionar arquivo" position="bottom" />
            <Tooltip target=".custom-cancel-btn" content="Limpar" position="bottom" />
            {editMode ?
                <FileUpload
                    ref={fileUploadRef}
                    name="doc" 
                    accept={accept || "image/*,application/pdf"}
                    maxFileSize={30000000}
                    onSelect={onTemplateSelect}
                    onError={onTemplateClear}
                    onClear={onTemplateClear}
                    headerTemplate={headerTemplate}
                    itemTemplate={itemTemplate}
                    emptyTemplate={emptyTemplate}
                    chooseOptions={chooseOptions}
                    cancelOptions={cancelOptions}
                />
                :
                (file && (typeof file === 'string' ? file.toLowerCase().endsWith('.pdf') : file.type === 'application/pdf') ?
                    <a href={file ? (typeof file === 'string' ? file : (file.objectURL || URL.createObjectURL(file))) : '#'} target="_blank" rel="noopener noreferrer">
                        <Document file={file}>
                            <Page pageNumber={1} width={250} />
                        </Document>
                    </a>
                    :
                    <Image src={file ? (typeof file === 'string' ? file : (file.objectURL || URL.createObjectURL(file))) : ''} indicatorIcon={<i className="pi pi-search" />} preview width='250' />)
            }
        </>
    );
}